// Change #100541 - Settings > Email > Sent Emails.
//
// Paul: "in settings do we have anywhere where we can see all outbound emails?
// when a food reservation diary request is made and i have setup the
// communication ... i want to check the email has gone out".
//
// Every email the company has sent through CRMit (EmailOutbound), newest first,
// with the Brevo delivery status and what it was sent about. The same component
// is rendered on Back End > Company Details > Sent Emails with adminCompanyId,
// which switches it onto the developer-only admin routes.
//
// Only sends Brevo ACCEPTED are stored, so a send that never left (switched off
// in Communications, no email address, provider refused it) is not here. For a
// booking, the reservation's own Activity log records those with the reason.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { isEmpty } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";

import TableContainer from "../../../../Components/Common/TableContainer";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import AppModal from "../../../../Components/Common/AppModal";
import EmailSandbox from "../../../../Components/Common/EmailSandbox";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import {
  sentEmailsDetailApiCall,
  sentEmailsResendApiCall,
  sentEmailsSearchApiCall,
} from "../../../../helpers/fakebackend_helper";

const STATUS = {
  SENT: { label: "Sent", color: "secondary" },
  DELIVERED: { label: "Delivered", color: "success" },
  OPENED: { label: "Opened", color: "primary" },
  DEFERRED: { label: "Deferred", color: "warning" },
  FAILED: { label: "Failed", color: "danger" },
};

const LINKED = {
  RESERVATION: "Reservation",
  PREORDER: "Pre-order event",
  INVOICE: "Invoice",
  REPLY: "Inbox reply",
};

// To / Cc / Bcc are stored as a JSON array; show them as a plain list so a
// cell never reads ["a@b.com"].
const formatAddresses = (value) => {
  if (!value) return "";
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter(Boolean).join(", ");
  } catch (e) {
    // Not JSON - already a plain address.
  }
  return String(value);
};

const linkedOf = (row) => {
  if (row.FoodTableReservation_Id)
    return `${LINKED.RESERVATION} #${row.FoodTableReservation_Id}`;
  if (row.FoodPreOrderEvent_Id)
    return `${LINKED.PREORDER} #${row.FoodPreOrderEvent_Id}`;
  if (row.Invoice_Id) return `${LINKED.INVOICE} #${row.Invoice_Id}`;
  if (row.EmailInbound_Id) return LINKED.REPLY;
  return "";
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.SENT;
  return <Badge color={s.color}>{s.label}</Badge>;
};

export default function TabSentEmails({ adminCompanyId }) {
  const [search, setSearch] = useState("");
  // Last 30 days by default: a busy venue sends thousands a month, and the
  // question is nearly always "did the one I just triggered go".
  const [dateFrom, setDateFrom] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [status, setStatus] = useState("");
  const [linked, setLinked] = useState("");

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filter = { Search: search.trim() };
      // New keys only when set (front and back deploy separately).
      if (dateFrom) filter.DateFrom = dateFrom;
      if (dateTo) filter.DateTo = dateTo;
      if (status) filter.Status = status;
      if (linked) filter.Linked = linked;

      const res = await sentEmailsSearchApiCall(filter, adminCompanyId);
      if (Number(res?.status) === 200) {
        setRows(res?.emailoutbound_list || []);
        setSummary(res);
      } else {
        setRows([]);
        setSummary(null);
        toast.error(res?.message || "Could not load sent emails");
      }
    } catch (e) {
      setRows([]);
      setSummary(null);
      toast.error("Could not load sent emails");
    }
    setLoading(false);
  }, [search, dateFrom, dateTo, status, linked, adminCompanyId]);

  // Debounced so typing in the search box fires one request, not one per key.
  useEffect(() => {
    const timer = setTimeout(() => load(), 350);
    return () => clearTimeout(timer);
  }, [load]);

  const openDetail = useCallback(
    async (row) => {
      setDetail(null);
      setDetailOpen(true);
      setDetailLoading(true);
      try {
        const res = await sentEmailsDetailApiCall(row.Id, adminCompanyId);
        if (Number(res?.status) === 200) {
          setDetail(res);
        } else {
          toast.error(res?.message || "Could not open this email");
          setDetailOpen(false);
        }
      } catch (e) {
        toast.error("Could not open this email");
        setDetailOpen(false);
      }
      setDetailLoading(false);
    },
    [adminCompanyId]
  );

  // Change #100765 - Resend: confirm with the recipients (editable), then send it again.
  const [resendRow, setResendRow] = useState(null);
  const [resendTo, setResendTo] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const openResend = useCallback((row) => {
    setResendRow(row);
    setResendTo(formatAddresses(row?.To));
  }, []);
  const doResend = async () => {
    if (!resendRow) return;
    const to = resendTo
      .split(/[,;\s]+/)
      .map((a) => a.trim())
      .filter(Boolean);
    if (!to.length) {
      toast.error("Enter at least one email address");
      return;
    }
    setResendBusy(true);
    try {
      const original = formatAddresses(resendRow.To);
      const changed = to.join(", ") !== original;
      const res = await sentEmailsResendApiCall(resendRow.Id, adminCompanyId, changed ? to : null);
      if (Number(res?.status) === 200) {
        toast.success(res?.message || "Email sent again");
        setResendRow(null);
        load();
      } else {
        toast.error(res?.message || "Could not resend the email");
      }
    } catch (e) {
      toast.error("Could not resend the email");
    }
    setResendBusy(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Sent",
        accessor: "CreatedDateTime",
        Cell: ({ row }) => (
          <span className="text-nowrap">
            {HandleDateTime(row.original.CreatedDateTime)}
          </span>
        ),
      },
      {
        Header: "To",
        accessor: "To",
        Cell: ({ row }) => (
          <span className="text-muted">{formatAddresses(row.original.To)}</span>
        ),
      },
      {
        Header: "Subject",
        accessor: "Subject",
        Cell: ({ row }) => (
          <span
            className="fw-medium text-body"
            role="button"
            onClick={() => openDetail(row.original)}
          >
            {row.original.Subject || (
              <span className="text-muted">(no subject)</span>
            )}
            {Number(row.original.FlagHasAttachment) === 1 && (
              <i
                className="ri-attachment-2 ms-1 text-muted"
                title="Has attachments"
              ></i>
            )}
          </span>
        ),
      },
      {
        Header: "From",
        accessor: "FromName",
        Cell: ({ row }) => (
          <span className="text-muted">
            {row.original.FromName || row.original.FromAddress || ""}
            {row.original.CreatedStaffName ? (
              <div className="fs-12">by {row.original.CreatedStaffName}</div>
            ) : null}
          </span>
        ),
      },
      {
        Header: "Related to",
        accessor: "FoodTableReservation_Id",
        Cell: ({ row }) => (
          <span className="text-muted">{linkedOf(row.original)}</span>
        ),
      },
      {
        Header: "Status",
        accessor: "SentStatus",
        Cell: ({ row }) => <StatusBadge status={row.original.SentStatus} />,
      },
      {
        Header: "",
        accessor: "Id",
        disableSortBy: true,
        Cell: ({ row }) => (
          <UncontrolledDropdown direction="start">
            <DropdownToggle tag="button" className="btn btn-soft-secondary btn-sm">
              <i className="ri-more-fill"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem onClick={() => openDetail(row.original)}>
                <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
                View email
              </DropdownItem>
              {/* Change #100765 */}
              <DropdownItem onClick={() => openResend(row.original)}>
                <i className="ri-send-plane-2-line align-bottom me-2 text-muted"></i>
                Resend
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    [openDetail, openResend]
  );

  const email = detail?.emailoutbound_data;

  return (
    <>
      {/* Change #100765 - resend confirmation */}
      {resendRow && (
        <AppModal
          open={!!resendRow}
          onClose={() => !resendBusy && setResendRow(null)}
          title="Resend email"
          headerAccent="indigo"
          size="sm"
          loading={resendBusy}
          onSubmit={doResend}
          submitLabel="Resend"
          submitColor="primary"
          cancelLabel="Cancel"
        >
          <p className="mb-2">
            <strong>{resendRow.Subject || "(no subject)"}</strong>
          </p>
          <p className="text-muted fs-13 mb-3">
            Sends the same email again - same sender, wording and attachments. It appears in this list as a new
            email with its own delivery status.
          </p>
          <label className="form-label mb-1" htmlFor="resend-to">
            Send to
          </label>
          <input
            id="resend-to"
            className="form-control"
            value={resendTo}
            onChange={(e) => setResendTo(e.target.value)}
            placeholder="name@example.com"
          />
          <div className="form-text">Change it to send to someone else. Separate several with commas.</div>
        </AppModal>
      )}
      {detailOpen && (
        <AppModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          title={email?.Subject || "Sent email"}
          headerAccent="indigo"
          size="lg"
          loading={detailLoading}
          hideCancel
          submitLabel="Close"
          onSubmit={() => setDetailOpen(false)}
        >
          {email && (
            <div>
              <Row className="g-2 mb-3 fs-13">
                <Col md={8}>
                  <div>
                    <span className="text-muted">To: </span>
                    {formatAddresses(email.To)}
                  </div>
                  {!isEmpty(formatAddresses(email.Cc)) && (
                    <div>
                      <span className="text-muted">Cc: </span>
                      {formatAddresses(email.Cc)}
                    </div>
                  )}
                  {!isEmpty(formatAddresses(email.Bcc)) && (
                    <div>
                      <span className="text-muted">Bcc: </span>
                      {formatAddresses(email.Bcc)}
                    </div>
                  )}
                  <div>
                    <span className="text-muted">From: </span>
                    {[email.FromName, email.FromAddress && `<${email.FromAddress}>`]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </div>
                  <div>
                    <span className="text-muted">Sent: </span>
                    {HandleDateTime(email.CreatedDateTime)}
                  </div>
                </Col>
                <Col md={4} className="text-md-end">
                  <StatusBadge status={email.SentStatus} />
                </Col>
              </Row>

              {!isEmpty(detail?.linked_list) && (
                <div className="mb-3 fs-13">
                  <span className="text-muted">Related to: </span>
                  {detail.linked_list.map((l) => (
                    <Badge
                      key={`${l.Type}_${l.Id}`}
                      color="light"
                      className="text-body me-1"
                    >
                      {l.Title}
                      {l.DateTime ? ` (${HandleDateTime(l.DateTime)})` : ""}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="mb-3">
                <h6 className="fs-13 mb-1">Delivery</h6>
                {isEmpty(detail?.emailoutboundevent_list) ? (
                  <p className="text-muted fs-13 mb-0">
                    Accepted by our email provider. No delivery updates have
                    come back for it yet.
                  </p>
                ) : (
                  <ul className="list-unstyled mb-0 fs-13">
                    {detail.emailoutboundevent_list.map((ev) => (
                      <li key={ev.Id}>
                        <span className="text-muted me-2">
                          {HandleDateTime(ev.CreatedDateTime)}
                        </span>
                        {ev.Title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {!isEmpty(detail?.attachment_list) && (
                <div className="mb-3">
                  <h6 className="fs-13 mb-1">Attachments</h6>
                  {detail.attachment_list.map((a, i) => (
                    <div key={`${a.FileName}_${i}`} className="fs-13">
                      <i className="ri-attachment-2 me-1 text-muted"></i>
                      {a.FileName}
                      {a.SizeBytes ? (
                        <span className="text-muted ms-1">
                          ({formatBytes(a.SizeBytes)})
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              <div
                className="border rounded overflow-hidden"
                style={{ height: 480 }}
              >
                <EmailSandbox html={email.BodyHtml || ""} />
              </div>
            </div>
          )}
        </AppModal>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader className="border-0">
              <Row className="g-2 align-items-end">
                <Col md={3}>
                  <div className="search-box">
                    <Input
                      type="search"
                      className="form-control search"
                      placeholder="Search to, subject, from..."
                      onChange={(e) => setSearch(e.target.value)}
                      value={search}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>
                <Col sm={6} md={2}>
                  <label className="form-label mb-1 fs-13">From date</label>
                  <Input
                    type="date"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Col>
                <Col sm={6} md={2}>
                  <label className="form-label mb-1 fs-13">To date</label>
                  <Input
                    type="date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Col>
                <Col sm={6} md={2}>
                  <label className="form-label mb-1 fs-13">Status</label>
                  <Input
                    type="select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="">All</option>
                    {Object.entries(STATUS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col sm={6} md={2}>
                  <label className="form-label mb-1 fs-13">Related to</label>
                  <Input
                    type="select"
                    value={linked}
                    onChange={(e) => setLinked(e.target.value)}
                  >
                    <option value="">Anything</option>
                    {Object.entries(LINKED).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                    <option value="NONE">Nothing</option>
                  </Input>
                </Col>
                <Col md={1} className="text-md-end">
                  <button
                    type="button"
                    className="btn btn-light w-100"
                    title="Refresh"
                    onClick={load}
                    disabled={loading}
                  >
                    <i className="ri-refresh-line"></i>
                  </button>
                </Col>
              </Row>
              {summary && Number(summary.FlagTruncated) === 1 && !loading && (
                <Alert color="info" className="mt-3 mb-0 fs-13">
                  {Number(summary.QtyTotal).toLocaleString()} emails match.
                  Showing the newest {Number(summary.QtyShown).toLocaleString()}.
                  Narrow the dates or search to see older ones.
                </Alert>
              )}
            </CardHeader>
            <CardBody className="pt-0">
              {loading ? (
                <LoadingSpinner />
              ) : !isEmpty(rows) ? (
                <TableContainer
                  columns={columns}
                  data={rows}
                  isGlobalFilter={false}
                  isAddUserList={false}
                  customPageSize={25}
                  className="custom-header-css"
                  divClass="table-responsive table-card mb-0"
                  tableClass="align-middle table-wrap"
                  theadClass="text-muted"
                  initialSortBy={[{ id: "CreatedDateTime", desc: true }]}
                  isLeadsFilter={false}
                />
              ) : (
                <NoDataContent
                  titleText="No sent emails found"
                  paragraphText={
                    "Nothing was sent that matches these filters. If you expected a booking email, open the booking and check its Activity log: it says whether the email was sent and, if not, why."
                  }
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}
